import type { PortalMeResponseDto } from '@supply-mover-2-0/shared';
import { authFetch, portalFetch } from '../lib/apiClient';
import { getBrandAlias } from '../lib/tenantResolver';

// --- Login server types (not shared DTOs since these are login-server-specific) ---

export interface TenantCheckResponse {
	valid: boolean;
	name: string;
	authOptions?: {
		phoneLoginEnabled: boolean;
	};
}

export interface PortalLoginResponse {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
	publicUserId: number;
	tenantAlias: string;
	languagePreference: string;
	/**
	 * PD-14144: when true, the portal password has aged past the
	 * `_passwordMaxAge_public` policy. No tokens are issued — the storefront
	 * sends the user into the login server's reset flow using `resetToken`.
	 */
	requiresPasswordChange?: boolean;
	resetToken?: string;
}

export interface SmsLoginRequestResponse {
	success: boolean;
	/** Seconds until the code expires (900 = 15 minutes) */
	expiresIn: number;
}

/**
 * Check whether a tenant alias maps to an active instance.
 * Returns the instance name for display and available auth options.
 * Throws on 404 (not found).
 */
export async function checkTenant(alias: string): Promise<TenantCheckResponse> {
	return authFetch<TenantCheckResponse>(`/tenant/${encodeURIComponent(alias)}`);
}

/**
 * Authenticate a portal user with email/password. Calls the login server.
 *
 * The login server prefers the brand parsed from the request hostname
 * (Origin/Referer/Host), but native shells route through CapacitorHttp,
 * which strips those headers — so the webview's storefront origin carries
 * no brand and the server would fall back to the tenant's DEFAULT company.
 * We therefore also send `brandAlias` (the build-baked brand natively, the
 * hostname-parsed brand on web) as an explicit fallback. This cannot
 * escalate access: the server still gates entry on the user's portal-user
 * binding (company + enabled client assignment).
 */
export async function login(
	email: string,
	password: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/login', {
		method: 'POST',
		body: JSON.stringify({
			email,
			password,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * Request an SMS verification code for passwordless login.
 * Always returns success to prevent phone number enumeration.
 */
export async function requestSmsCode(
	phoneNumber: string,
	tenantAlias: string,
): Promise<SmsLoginRequestResponse> {
	return authFetch<SmsLoginRequestResponse>('/sms-login/request', {
		method: 'POST',
		body: JSON.stringify({
			phoneNumber,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * Verify an SMS code and authenticate.
 * Returns the same token response as password-based login.
 *
 * Brand is preferred from the request hostname server-side; we also send
 * `brandAlias` as the native fallback (see `login` above) since CapacitorHttp
 * strips the origin headers the server would otherwise parse.
 */
export async function verifySmsCode(
	phoneNumber: string,
	code: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/sms-login/verify', {
		method: 'POST',
		body: JSON.stringify({
			phoneNumber,
			code,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * Fetch the currently authenticated portal user profile, clients, and active client.
 */
export async function me(token: string): Promise<PortalMeResponseDto> {
	return portalFetch<PortalMeResponseDto>('/auth/me', token);
}

/**
 * Set the active client for the current portal session.
 * Returns the confirmed activeClientId.
 */
export async function selectClient(
	token: string,
	clientId: number,
): Promise<{ activeClientId: number }> {
	return portalFetch<{ activeClientId: number }>('/auth/select-client', token, {
		method: 'POST',
		body: JSON.stringify({ clientId }),
	});
}
